import type { FC, ReactNode, RefObject } from 'react'
import type { Scene } from '@/types'

import { useEffect } from 'react'
import { ScrollTrigger } from '@/lib/motion'

interface SceneControllerProps {
    rootRef: RefObject<HTMLDivElement | null>
    initial: Scene
    routeKey: string
}

// Switches the tonal environment of the whole page (the data-theme attribute
// on .theme-root) as each [data-scene] section crosses the middle of the
// viewport. The --env-* custom properties then interpolate in CSS.
const SceneController: FC<SceneControllerProps> = ({ rootRef, initial, routeKey }): ReactNode => {
    useEffect(() => {
        const root = rootRef.current

        if (!root) return

        root.dataset.theme = initial

        // Scenes can nest (a marker inside a section takes over part of it), so
        // the innermost / latest active scene in document order always wins.
        // Markers hidden at the current breakpoint (display: none) are skipped.
        const sections = Array.from(document.querySelectorAll<HTMLElement>('main [data-scene]'))
            .filter(section => section.getClientRects().length > 0)
        const active = new Set<HTMLElement>()

        const apply = (): void => {
            const current = sections.filter(section => active.has(section)).pop()
            root.dataset.theme = current?.dataset.scene || initial
        }

        const triggers = sections.map(section => ScrollTrigger.create({
            trigger: section,
            start: 'top 52%',
            end: 'bottom 52%',
            onToggle: (self) => {
                if (self.isActive) active.add(section)
                else active.delete(section)

                apply()
            }
        }))

        return () => {
            triggers.forEach(trigger => trigger.kill())
        }
    }, [rootRef, initial, routeKey])

    return null
}

export default SceneController
